"use client";

import React, { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { ArrowRight, Download, Mail } from "lucide-react";
export default function Biography() {
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth >= 1024) {
        setOffset(window.scrollY * 0.1);
      } else {
        setOffset(0);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="26812" id="about-andrii" className="bg-background text-foreground py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        {}
        <div ref={imageRef} className="relative lg:sticky lg:top-24 z-10" style={{
        transform: `translateY(${offset}px)`
      }}>
          <div className="relative group perspective-1000">
            {}
            <div className={cn("relative rounded-sm overflow-hidden transition-all duration-1500 ease-out transform-gpu", isImageVisible ? "grayscale-0" : "grayscale filter")}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/portrait_elena_vostrova.png" alt="Andrii Executive Portrait" className="w-full h-auto object-cover shadow-2xl aspect-[3/4]" />
              
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>

            {}
            <div className="absolute -bottom-6 left-4 right-4 h-4 bg-black/40 blur-xl rounded-[100%] opacity-60 pointer-events-none" />
          </div>
        </div>

        {}
        <div className="flex flex-col gap-8 pt-8 lg:pt-0 relative z-20 bg-card border-t border-primary/20 shadow-2xl rounded-sm">
          {}
          <blockquote className="border-l-4 border-primary pl-6 py-2 my-4">
            <p className="font-serif font-default font-semibold text-4xl md:text-5xl leading-tight text-foreground">
              About Andrii
            </p>
          </blockquote>

          {}
          <div className="font-sans font-default text-lg leading-relaxed text-muted-foreground space-y-6">
            <p>
              "Excellence is not a destination, but a standard of movement that defines every journey we undertake." For over two decades, I have mastered the art of luxury transit, navigating the intersection of precision logistics and high-end hospitality. My path began in the corridors of elite service, learning how silence and punctuality define the ultimate{" "}
              <Link to="/ServicesPage" className="text-primary font-medium underline decoration-primary/30 hover:decoration-primary transition-colors">
                Bespoke Experience
              </Link>
              , crafting routes that are as seamless as they are safe.
            </p>

            <p>
              My philosophy is rooted in the belief that transportation is an act of trust. Whether I am orchestrating a complex{" "}
              <Link to="/CorporatePage" className="text-primary font-medium underline decoration-primary/30 hover:decoration-primary transition-colors">
                Corporate Strategy
              </Link>{" "}
              for global executives or managing the delicate timing of a private event, my goal remains constant: to eliminate stress and provide sanctuary. I work with the most discerning clients to transform simple travel into a refined, tangible luxury.
            </p>

            <p>
              Beyond the wheel, I am a curator of performance and a student of engineering. I believe that an{" "}
              <Link to="/OurFleetAboutUsContactPage" className="text-primary font-medium underline decoration-primary/30 hover:decoration-primary transition-colors">
                Elite Fleet
              </Link>{" "}
              is the foundation of reliability and prestige. When I am not overseeing operations, you can find me exploring the architectural heritage of San Francisco or refining my knowledge of automotive innovation to ensure our standards remain unmatched.
            </p>
          </div>

          {}
          <div className="flex flex-wrap gap-4 pt-4">
            <Button size="lg" className="rounded-full px-8 bg-primary text-primary-foreground hover:opacity-90">
              <Mail className="mr-2 h-4 w-4" />
              Reserve Your Journey
            </Button>
            <Button variant="outline" size="lg" className="rounded-full px-8 border-primary text-primary hover:bg-primary/5">
              <Download className="mr-2 h-4 w-4" />
              View Our Fleet
            </Button>
          </div>

          {}
          <div className="pt-8 mt-8 border-t border-border">
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <span className="font-serif font-default text-xl italic text-foreground">
                  Andrii — Founder
                </span>
                <span className="text-sm text-muted-foreground uppercase tracking-widest">
                  Principal Executive
                </span>
              </div>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/signature_elena.png" alt="Andrii Signature" className="h-12 opacity-80 ml-auto grayscale brightness-150" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}